import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import toast, { Toaster } from 'react-hot-toast';
import { sanitizeInput } from '../../services/securityService';
import { logAuditEvent } from '../../services/auditLogService';
import { ALL_SPECIALIZATIONS } from '../../utils/specializations';

// ─── Filterable specialty picker ─────────────────────────────────────────────
const SpecialtyPicker = ({ selected, onChange, disabled }) => {
  const [filter, setFilter] = useState('');
  const filtered = ALL_SPECIALIZATIONS.filter(s =>
    s.toLowerCase().includes(filter.toLowerCase())
  );
  const toggle = (specialty) => {
    if (selected.includes(specialty)) {
      onChange(selected.filter(s => s !== specialty));
    } else {
      onChange([...selected, specialty]);
    }
  };
  return (
    <div className="space-y-2">
      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map(s => (
            <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
              {s}
              {!disabled && (
                <button type="button" onClick={() => toggle(s)} className="hover:text-error ml-0.5">
                  <Icon name="X" className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {/* Filter input */}
      <div className="relative">
        <Icon name="Search" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-secondary" />
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Search specializations..."
          disabled={disabled}
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-border text-sm text-foreground placeholder:text-foreground-secondary focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
        />
      </div>
      {/* Option list */}
      <div className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border">
        {filtered.length === 0 ? (
          <p className="px-3 py-2 text-sm text-foreground-secondary">No results</p>
        ) : filtered.map(s => {
          const isSelected = selected.includes(s);
          return (
            <button
              key={s}
              type="button"
              disabled={disabled}
              onClick={() => toggle(s)}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors
                ${isSelected ? 'bg-primary/5 text-primary font-medium' : 'text-foreground hover:bg-muted'}`}
            >
              {s}
              {isSelected && <Icon name="Check" className="w-4 h-4 flex-shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Role selection card ──────────────────────────────────────────────────────
const RoleCard = ({ role, icon, title, description, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(role)}
    className={`w-full text-left rounded-2xl border-2 p-6 transition-all duration-200 flex gap-4 items-start
      ${selected
        ? 'border-primary bg-primary/5 shadow-md'
        : 'border-border bg-white hover:border-primary/50'
      }`}
  >
    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center
      ${selected ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}>
      <Icon name={icon} className="w-6 h-6" />
    </div>
    <div className="flex-1">
      <p className={`font-semibold text-lg mb-1 ${selected ? 'text-primary' : 'text-foreground'}`}>{title}</p>
      <p className="text-sm text-foreground-secondary leading-snug">{description}</p>
    </div>
    {selected && (
      <Icon name="CheckCircle" className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
    )}
  </button>
);

// ─── Main component ───────────────────────────────────────────────────────────
const SignUp = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();

  // step: 'role' | 'form'
  const [step, setStep] = useState('role');
  const [selectedRole, setSelectedRole] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Common fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Expert-only fields
  const [title, setTitle] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [yearsExperience, setYearsExperience] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [bio, setBio] = useState('');

  const handleRoleContinue = () => {
    if (!selectedRole) {
      toast.error('Please select an account type to continue');
      return;
    }
    setStep('form');
  };

  const validateForm = () => {
    if (!fullName.trim() || !email.trim() || !password || !confirmPassword) {
      toast.error('Please fill in all required fields');
      return false;
    }
    if (selectedRole === 'expert' && !title.trim()) {
      toast.error('Please enter your professional title');
      return false;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setErrorMessage('');
    setIsLoading(true);
    try {
      const sanitizedEmail = sanitizeInput(email);
      const sanitizedFullName = sanitizeInput(fullName);
      const sanitizedPassword = sanitizeInput(password);

      const expertData = selectedRole === 'expert' ? {
        title: sanitizeInput(title),
        bio: sanitizeInput(bio),
        specialties,
        years_experience: yearsExperience ? parseInt(yearsExperience, 10) : null,
        hourly_rate: hourlyRate ? parseFloat(hourlyRate) : null,
      } : null;

      const { data, error } = await signUp(
        sanitizedEmail,
        sanitizedPassword,
        sanitizedFullName,
        null,         // testAccountCode
        selectedRole,
        expertData
      );

      if (error) {
        const msg = error?.message || '';
        const friendlyMsg = (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('user already exists'))
          ? 'An account with this email already exists. Please sign in instead.'
          : (msg || 'Signup failed. Please try again.');
        setErrorMessage(friendlyMsg);
        toast.error(friendlyMsg, { id: 'signup-error' });
        setIsLoading(false);
        return;
      }

      // Supabase returns a fake success for existing emails to prevent enumeration.
      // The tell-tale sign is an empty identities array.
      if (!data?.user?.identities || data.user.identities.length === 0) {
        const msg = 'An account with this email already exists. Please sign in instead.';
        setErrorMessage(msg);
        toast.error(msg, { id: 'signup-error' });
        setIsLoading(false);
        return;
      }

      await logAuditEvent({
        actionType: 'signup',
        actionDescription: `New ${selectedRole} registered: ${sanitizedEmail}`,
        severity: 'info'
      });

      toast.success('Account created! Please verify your email to continue.');
      navigate('/verify-email-required');
    } catch (err) {
      console.error('Signup error:', err);
      const msg = 'An unexpected error occurred. Please try again.';
      setErrorMessage(msg);
      toast.error(msg, { id: 'signup-error' });
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10 px-4 py-12">
      <Toaster position="top-right" />
      <div className="w-full max-w-lg">

        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-16 object-contain mx-auto mb-4" />
          {step === 'role' ? (
            <>
              <h1 className="text-3xl font-bold text-foreground mb-2">Join FireCaseIQ</h1>
              <p className="text-foreground-secondary">How will you be using the platform?</p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {selectedRole === 'expert' ? 'Expert Account' : 'Client Account'}
              </h1>
              <p className="text-foreground-secondary">
                {selectedRole === 'expert'
                  ? 'Tell us about your professional background'
                  : 'Fill in your details to get started'}
              </p>
            </>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-border">

          {/* ── STEP 1: role selection ── */}
          {step === 'role' && (
            <div className="space-y-4">
              <RoleCard
                role="client"
                icon="User"
                title="I'm a Client"
                description="I need forensic investigation services for my case. I want to submit evidence and work with certified experts."
                selected={selectedRole === 'client'}
                onSelect={setSelectedRole}
              />
              <RoleCard
                role="expert"
                icon="Briefcase"
                title="I'm an Expert"
                description="I'm a forensic professional offering investigation services. I want to review cases and provide expert analysis."
                selected={selectedRole === 'expert'}
                onSelect={setSelectedRole}
              />

              <Button
                onClick={handleRoleContinue}
                className="w-full mt-2 bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 transition-smooth flex items-center justify-center gap-2"
              >
                Continue
                <Icon name="ArrowRight" className="w-4 h-4" />
              </Button>

              <p className="text-sm text-foreground-secondary text-center pt-2">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:text-primary/80 font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          )}

          {/* ── STEP 2: registration form ── */}
          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Back + role badge */}
              <div className="flex items-center justify-between mb-1">
                <button
                  type="button"
                  onClick={() => setStep('role')}
                  className="flex items-center gap-1 text-sm text-foreground-secondary hover:text-foreground"
                >
                  <Icon name="ArrowLeft" className="w-4 h-4" />
                  Change account type
                </button>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium
                  ${selectedRole === 'expert' ? 'bg-secondary/10 text-secondary' : 'bg-primary/10 text-primary'}`}>
                  <Icon name={selectedRole === 'expert' ? 'Briefcase' : 'User'} className="w-3.5 h-3.5" />
                  {selectedRole === 'expert' ? 'Expert' : 'Client'}
                </span>
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Full Name <span className="text-error">*</span>
                </label>
                <Input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e?.target?.value)}
                  placeholder="John Doe"
                  disabled={isLoading}
                  className="w-full"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address <span className="text-error">*</span>
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e?.target?.value); setErrorMessage(''); }}
                  placeholder="you@example.com"
                  disabled={isLoading}
                  className="w-full"
                />
              </div>

              {/* Expert-only fields */}
              {selectedRole === 'expert' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Professional Title <span className="text-error">*</span>
                    </label>
                    <Input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e?.target?.value)}
                      placeholder="e.g. Fire Cause & Origin Investigator"
                      disabled={isLoading}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Areas of Specialization{' '}
                      <span className="text-xs text-foreground-secondary font-normal">({specialties.length} selected)</span>
                    </label>
                    <SpecialtyPicker
                      selected={specialties}
                      onChange={setSpecialties}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Years of Experience
                    </label>
                    <Input
                      type="number"
                      min="0"
                      max="60"
                      value={yearsExperience}
                      onChange={(e) => setYearsExperience(e?.target?.value)}
                      placeholder="e.g. 10"
                      disabled={isLoading}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Hourly Rate (USD)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-secondary text-sm">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={hourlyRate}
                        onChange={(e) => setHourlyRate(e?.target?.value)}
                        placeholder="0.00"
                        disabled={isLoading}
                        className="w-full pl-7"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Professional Bio
                    </label>
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e?.target?.value)}
                      placeholder="Briefly describe your expertise, background and experience..."
                      disabled={isLoading}
                      rows={3}
                      className="w-full rounded-lg border border-border px-3 py-2 text-sm text-foreground placeholder:text-foreground-secondary focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 resize-none"
                    />
                  </div>
                </>
              )}

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Password <span className="text-error">*</span>
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    placeholder="Min. 8 characters"
                    disabled={isLoading}
                    className="w-full pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-secondary hover:text-foreground"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Confirm Password <span className="text-error">*</span>
                </label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e?.target?.value)}
                  placeholder="••••••••"
                  disabled={isLoading}
                  className="w-full"
                />
              </div>

              {/* Terms */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e?.target?.checked)}
                  className="rounded border-border text-primary focus:ring-primary mt-0.5"
                />
                <label htmlFor="terms" className="text-sm text-foreground-secondary">
                  I agree to the{' '}
                  <Link to="/terms-of-service" className="text-primary hover:text-primary/80">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy-policy" className="text-primary hover:text-primary/80">Privacy Policy</Link>
                </label>
              </div>

              {errorMessage && (
                <div className="flex items-start gap-2 rounded-lg bg-error/10 border border-error/30 px-4 py-3">
                  <Icon name="AlertCircle" className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-error">{errorMessage}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating account...' : 'Create Account'}
              </Button>

              <p className="text-sm text-foreground-secondary text-center">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:text-primary/80 font-medium">
                  Sign in
                </Link>
              </p>
            </form>
          )}
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-foreground-secondary hover:text-foreground flex items-center justify-center">
            <Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignUp;